/**
 * Lightweight, theme-aware charts built with plain elements (no chart library).
 * Single-series magnitude bars: one hue (primary), thin marks with rounded data
 * ends, values as text tokens, a hover tooltip per mark and a table elsewhere
 * on the page for exact numbers.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { formatPercent } from '@/utils/format';
import { performanceTone } from '../utils';

const BAR = 12; // horizontal bar thickness (px)

/** Horizontal percentage bar (0–100) with an optional comparison marker (e.g. class average). */
export function PercentBar({
  value,
  compare,
  compareLabel = 'Média da turma',
  label,
  tone = 'primary',
}: {
  value: number | null;
  compare?: number | null;
  compareLabel?: string;
  label: string;
  tone?: 'primary' | 'success' | 'warning' | 'error';
}) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  return (
    <Tooltip
      title={
        <>
          {label}: {formatPercent(value, 1)}
          {compare !== undefined && compare !== null && (
            <>
              <br />
              {compareLabel}: {formatPercent(compare, 1)}
            </>
          )}
        </>
      }
      placement="top"
    >
      <Box
        role="img"
        aria-label={`${label}: ${formatPercent(value, 1)}`}
        sx={(theme) => ({
          position: 'relative',
          height: BAR,
          borderRadius: 99,
          bgcolor: theme.alpha(theme.vars.palette[tone].main, 0.14),
          flex: 1,
          minWidth: 60,
        })}
      >
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            width: `${pct}%`,
            borderRadius: 99,
            bgcolor: theme.vars.palette[tone].main,
            transition: 'width 500ms cubic-bezier(0.05, 0.7, 0.1, 1)',
          })}
        />
        {compare !== undefined && compare !== null && (
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: -4,
              bottom: -4,
              left: `calc(${Math.max(0, Math.min(100, compare))}% - 1.5px)`,
              width: 3,
              borderRadius: 2,
              bgcolor: theme.vars.palette.text.primary,
              boxShadow: `0 0 0 2px ${theme.vars.palette.background.paper}`,
            })}
          />
        )}
      </Box>
    </Tooltip>
  );
}

/** Category (or any labelled) performance list: label · bar · value. */
export function PerformanceList({
  rows,
  compareLabel,
  empty = 'Sem dados ainda.',
}: {
  rows: {
    key: string;
    label: ReactNode;
    sublabel?: ReactNode;
    percent: number | null;
    compare?: number | null;
    indent?: number;
  }[];
  compareLabel?: string;
  empty?: string;
}) {
  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        {empty}
      </Typography>
    );
  }
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 56px', sm: 'minmax(140px, 38%) 1fr 56px' },
            gap: { xs: 0.75, sm: 2 },
            alignItems: 'center',
            pl: { sm: (row.indent ?? 0) * 2 },
          }}
        >
          <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / 2', sm: 'auto' } }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: row.indent ? 400 : 600 }}>
              {row.label}
            </Typography>
            {row.sublabel && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {row.sublabel}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              gridColumn: { xs: '1 / -1', sm: 'auto' },
              gridRow: { xs: 2, sm: 'auto' },
              display: 'flex',
            }}
          >
            <PercentBar
              value={row.percent}
              compare={row.compare}
              compareLabel={compareLabel}
              label={typeof row.label === 'string' ? row.label : 'Desempenho'}
              tone={performanceTone(row.percent)}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              textAlign: 'right',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              gridRow: { xs: 1, sm: 'auto' },
              gridColumn: { xs: 2, sm: 'auto' },
            }}
          >
            {formatPercent(row.percent)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** Score distribution: 10 columns (0–10%, …, 90–100%). */
export function Histogram({
  bins,
  passing,
  marker,
  unit = 'estudante',
}: {
  bins: { from: number; to: number; count: number }[];
  passing?: number | null;
  /** A single score to point at, e.g. the student's own ("você"). */
  marker?: { value: number; label: string } | null;
  unit?: string;
}) {
  const max = Math.max(1, ...bins.map((b) => b.count));
  const HEIGHT = 140;
  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          height: HEIGHT + 24,
          display: 'flex',
          alignItems: 'flex-end',
          gap: '2px',
          pt: 3,
        }}
      >
        {passing !== undefined && passing !== null && (
          <Tooltip title={`Nota de aprovação: ${passing}%`}>
            <Box
              sx={(theme) => ({
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${passing}%`,
                borderLeft: `2px dashed ${theme.vars.palette.text.disabled}`,
                zIndex: 1,
              })}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ position: 'absolute', top: -2, left: 6, whiteSpace: 'nowrap' }}
              >
                aprovação
              </Typography>
            </Box>
          </Tooltip>
        )}
        {marker && (
          <Tooltip title={`${marker.label}: ${formatPercent(marker.value, 1)}`}>
            <Box
              sx={(theme) => ({
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${Math.max(0, Math.min(100, marker.value))}%`,
                borderLeft: `2px solid ${theme.vars.palette.text.primary}`,
                zIndex: 1,
              })}
            >
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: -2,
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  // Near the right edge the label goes to the left of the line.
                  ...(marker.value > 80 ? { right: 6 } : { left: 6 }),
                }}
              >
                {marker.label}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {bins.map((bin) => {
          const h = bin.count ? Math.max(6, (bin.count / max) * HEIGHT) : 0;
          return (
            <Tooltip
              key={bin.from}
              title={`${bin.from}–${bin.to}%: ${bin.count} ${bin.count === 1 ? unit : `${unit}s`}`}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  height: '100%',
                  cursor: 'default',
                }}
              >
                {bin.count > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, mb: 0.5, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {bin.count}
                  </Typography>
                )}
                <Box
                  sx={(theme) => ({
                    width: '100%',
                    maxWidth: 24,
                    height: h,
                    borderRadius: '4px 4px 0 0',
                    bgcolor:
                      bin.from >= (passing ?? 101)
                        ? theme.vars.palette.primary.main
                        : theme.alpha(theme.vars.palette.primary.main, 0.55),
                    transition: 'height 500ms cubic-bezier(0.05, 0.7, 0.1, 1)',
                  })}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: '2px',
          borderTop: `1px solid ${theme.vars.palette.divider}`,
          pt: 0.5,
        })}
      >
        {bins.map((bin) => (
          <Typography
            key={bin.from}
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
          >
            {bin.from}
          </Typography>
        ))}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ textAlign: 'center', mt: 0.5 }}
      >
        Nota (%)
      </Typography>
    </Box>
  );
}

/** Distribution of answers across options; the correct one is marked with an icon + label. */
export function OptionBars({
  options,
}: {
  options: {
    id: string;
    letter: string;
    text: string;
    isCorrect: boolean;
    count: number;
    percent: number;
  }[];
}) {
  return (
    <Stack spacing={1}>
      {options.map((o) => (
        <Box
          key={o.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: '28px 1fr 64px',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: o.isCorrect ? 'success.main' : 'text.secondary' }}
          >
            {o.letter}
          </Typography>
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
              {o.isCorrect && (
                <CheckCircleRounded color="success" sx={{ fontSize: 16 }} aria-label="Correta" />
              )}
              <Typography variant="caption" color="text.secondary" noWrap title={o.text}>
                {o.isCorrect ? 'Correta · ' : ''}
                {o.text}
              </Typography>
            </Stack>
            <PercentBar
              value={o.percent}
              label={`Alternativa ${o.letter}`}
              tone={o.isCorrect ? 'success' : 'primary'}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {o.count} · {formatPercent(o.percent)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** Circular score display for results. */
export function ScoreRing({
  percent,
  size = 132,
  label,
}: {
  percent: number | null;
  size?: number;
  label?: string;
}) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const value = Math.max(0, Math.min(100, percent ?? 0));
  const tone = performanceTone(percent);
  return (
    <Box
      sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      role="img"
      aria-label={`Nota: ${formatPercent(percent)}`}
    >
      <Box
        component="svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        sx={{ transform: 'rotate(-90deg)', display: 'block' }}
      >
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          sx={(theme) => ({ stroke: theme.alpha(theme.vars.palette[tone].main, 0.16) })}
        />
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - value / 100)}
          sx={(theme) => ({
            stroke: theme.vars.palette[tone].main,
            transition: 'stroke-dashoffset 900ms cubic-bezier(0.05, 0.7, 0.1, 1)',
          })}
        />
      </Box>
      <Stack
        sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}
      >
        <Typography
          sx={{
            fontSize: size * 0.24,
            fontWeight: 700,
            lineHeight: 1,
            fontVariationSettings: "'ROND' 100",
          }}
        >
          {percent === null ? '—' : `${Math.round(value)}%`}
        </Typography>
        {label && (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export interface TrendPoint {
  key: string;
  /** Short x label, e.g. "1ª" or "15/09". */
  label: string;
  value: number | null;
  /** Longer text for the tooltip, e.g. the date or the quiz. */
  detail?: string;
  /** Emphasized (e.g. the attempt being shown). */
  highlight?: boolean;
}

/**
 * Percentage (0–100) over a sequence: attempts, weeks. One series, one hue, a 2px line with
 * markers, and a crosshair + tooltip on hover (or tap). Keep a table with the exact numbers
 * nearby for screen readers.
 */
export function TrendChart({
  points,
  valueLabel,
  height = 190,
  reference,
}: {
  points: TrendPoint[];
  valueLabel: string;
  height?: number;
  /** Horizontal guide, e.g. the passing score or the class average. */
  reference?: { value: number; label: string } | null;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const left = 34;
  const right = 14;
  const top = 12;
  const bottom = 26;
  const plotW = Math.max(0, width - left - right);
  const plotH = height - top - bottom;
  const n = points.length;
  const x = (i: number) => left + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const y = (v: number) => top + plotH * (1 - Math.max(0, Math.min(100, v)) / 100);
  const drawn = points.map((p, i) => ({ ...p, i })).filter((p) => p.value !== null);
  const path = drawn.map((p, k) => `${k ? 'L' : 'M'}${x(p.i)},${y(p.value!)}`).join(' ');
  // Every label when they fit; otherwise the first, the last and the highlighted ones.
  const everyLabel = n <= Math.max(2, Math.floor(plotW / 44));
  const active = hover !== null ? points[hover] : null;

  const pick = (clientX: number, box: DOMRect) => {
    if (n === 0) return;
    const at = clientX - box.left - left;
    const i = n <= 1 ? 0 : Math.round((at / plotW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  const summary = drawn.map((p) => `${p.label}: ${formatPercent(p.value, 1)}`).join(', ');

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%', height }}>
      {width > 0 && (
        <Box
          component="svg"
          width={width}
          height={height}
          role="img"
          aria-label={`${valueLabel}. ${summary}`}
          sx={{ display: 'block', overflow: 'visible', touchAction: 'pan-y' }}
          onPointerMove={(e: React.PointerEvent<SVGSVGElement>) =>
            pick(e.clientX, e.currentTarget.getBoundingClientRect())
          }
          onPointerDown={(e: React.PointerEvent<SVGSVGElement>) =>
            pick(e.clientX, e.currentTarget.getBoundingClientRect())
          }
          onPointerLeave={() => setHover(null)}
        >
          {[0, 25, 50, 75, 100].map((tick) => (
            <g key={tick}>
              <Box
                component="line"
                x1={left}
                x2={left + plotW}
                y1={y(tick)}
                y2={y(tick)}
                sx={(t) => ({ stroke: t.vars.palette.divider, strokeWidth: 1 })}
              />
              {tick % 50 === 0 && (
                <Box
                  component="text"
                  x={left - 8}
                  y={y(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  sx={(t) => ({ fill: t.vars.palette.text.secondary, fontSize: 11 })}
                >
                  {tick}%
                </Box>
              )}
            </g>
          ))}
          {reference && (
            <g>
              <Box
                component="line"
                x1={left}
                x2={left + plotW}
                y1={y(reference.value)}
                y2={y(reference.value)}
                strokeDasharray="4 4"
                sx={(t) => ({ stroke: t.vars.palette.text.disabled, strokeWidth: 1.5 })}
              />
              <Box
                component="text"
                x={left + plotW}
                y={y(reference.value) - 6}
                textAnchor="end"
                sx={(t) => ({ fill: t.vars.palette.text.secondary, fontSize: 11 })}
              >
                {reference.label}
              </Box>
            </g>
          )}
          {active && hover !== null && (
            <Box
              component="line"
              x1={x(hover)}
              x2={x(hover)}
              y1={top}
              y2={top + plotH}
              sx={(t) => ({ stroke: t.vars.palette.text.disabled, strokeWidth: 1 })}
            />
          )}
          <Box
            component="path"
            d={path}
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
            sx={(t) => ({ stroke: t.vars.palette.primary.main, strokeWidth: 2 })}
          />
          {drawn.map((p) => (
            <Box
              key={p.key}
              component="circle"
              cx={x(p.i)}
              cy={y(p.value!)}
              r={p.highlight || hover === p.i ? 6 : 4}
              sx={(t) => ({
                fill: t.vars.palette.primary.main,
                stroke: t.vars.palette.background.paper,
                strokeWidth: 2,
                transition: 'r 120ms',
              })}
            />
          ))}
          {points.map((p, i) =>
            everyLabel || i === 0 || i === n - 1 || p.highlight ? (
              <Box
                key={p.key}
                component="text"
                x={x(i)}
                y={height - 6}
                textAnchor={n > 1 && i === 0 ? 'start' : n > 1 && i === n - 1 ? 'end' : 'middle'}
                sx={(t) => ({
                  fill: p.highlight ? t.vars.palette.text.primary : t.vars.palette.text.secondary,
                  fontSize: 11,
                  fontWeight: p.highlight ? 600 : 400,
                })}
              >
                {p.label}
              </Box>
            ) : null,
          )}
        </Box>
      )}
      {active && hover !== null && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 0,
            left: Math.min(Math.max(x(hover) - 110, 0), Math.max(0, width - 220)),
            width: 220,
            px: 1.25,
            py: 0.75,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {active.detail ?? active.label}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {valueLabel}: {formatPercent(active.value, 1)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
